import QuizStatuses from '@/constant/QuizStatuses'
import constants from '@/constant'
import IdeasTabName from '@/constant/IdeasTabName'
import utils from '@/util'
import { isColorLight, isContrast } from '@/utils/color.utils'
import renderCanvasBlocksUtils from '@/utils/renderCanvasBlocksUtils'

const { defaultQuestionTitle, defaultHeadingTitle } = constants

const lastSlide = (state, _, rootGetters) => {
  if (rootGetters['skipSlide/canUseSkipSlide']) {
    const visibleSlides = rootGetters['skipSlide/visibleSlides']
    return visibleSlides?.length ? visibleSlides[visibleSlides.length - 1] : null
  }

  const { slides } = state.presentation
  return slides.length ? slides[slides.length - 1] : null
}

const isLastSlide = (state, getters) => {
  return state.slideActive.id
    && getters.lastSlide
    && getters.lastSlide.id === state.slideActive.id
}

const isStartPresentingFromLastSlide = (state, getters) => {
  return state.startPresentingFromSlideId === getters.lastSlide?.id
}

/**
 * @deprecated use slideActive getter inside slide.module instead
 */
const getSlideActive = (state) => {
  return state.slideActive
}

const isLobbyScreen = (state) => {
  return state.slideActive.quizStatus == QuizStatuses.LOBBY_SCREEN
}

const questionHasImageXLarge = (state) => {
  return !!(state.slideActive.image && state.slideActive.image.xLarge)
}

export const isTextColourLight = (_, __, ___, rootGetters) => {
  const slideActive = rootGetters['slideModule/slideActive']
  return isColorLight(slideActive.textColour)
}

const isQuestionImageEnabled = (state) => {
  let isEnable = state.slideActive.type === constants.SLIDE_TYPE.PICK_ANSWER && (
    state.slideActive.slideType === constants.SLIDE_TYPE.TYPE_ANSWER || !state.slideActive.slideType)

  // Always enable this feature (previously behind feature toggle)
  isEnable = isEnable || state.slideActive.slideType === constants.SLIDE_TYPE.PICK_IMAGE

  return isEnable
}

const isQuestionImageAndHasImageXLargeEnabled = (state) => {
  return questionHasImageXLarge(state) && isQuestionImageEnabled(state)
}

const isShowFullImageQuestion = (state) => {
  return state.slideActive.showFullImageQuestion && questionHasImageXLarge(state)
}

const hasSlideId = (state) => {
  return state.slideActive?.id ?? false
}

const isContentSlide = (_, getters) => {
  return getters['slideType/isContentSlide']
}

const currentSlideType = (state) => {
  return utils.getCorrectSlideTypeName(state.slideActive.type, state.slideActive.slideType)
}

const hasSlideTitle = (state) => {
  return hasSlideId(state) && state.slideActive?.title?.length > 0
}

const hasDefaultTitle = (state) => {
  const defaultTitles = [defaultHeadingTitle, defaultQuestionTitle]

  return hasSlideTitle(state) && defaultTitles.includes(state.slideActive.title.trim())
}

const slideTitle = (_, __, rootState) => {
  return rootState?.slideActive?.title
}
const hasDefaultCanvasBlockLayout = (state) => {
  if (!state.slideActive || !state.slideActive.canvasBlocks) return false

  return renderCanvasBlocksUtils.isDefaultCanvasBlocks(state.slideActive.canvasBlocks)
}

const isDefaultSlide = (state, getters) => {
  if (isContentSlide(state, getters)) {
    return hasDefaultCanvasBlockLayout(state)
  }

  return !hasSlideTitle(state) || hasDefaultTitle(state)
}
const isQuizSlideType = (state, getters, rootState, rootGetters) => {
  const { id } = state.slideActive
  return Boolean(id) && rootGetters['slideModule/isQuizSlide'](state.slideActive)
}
const isSlideTitleEmpty = (state) => {
  return !state.slideActive.title || !state.slideActive.title.trim()
}

const isContrastEnough = (state, _, __, rootGetters) => {
  const currentPresentationTheme = rootGetters['themeModule/currentPresentationTheme']
  const { slideActive } = state

  const textColour = slideActive?.textColour || currentPresentationTheme?.textColour
  const baseColour = slideActive?.baseColour || currentPresentationTheme?.baseColour

  return isContrast(textColour, baseColour)
}

const isTypeAnswerButtonVisible = (state, getters) => {
  if (state.sharePresentation) return false
  const {
    type, slideType, quizStatus, showCorrectOption,
  } = state.slideActive
  const { manualRevealCorrectAnswers } = state.presentation
  return type === 'pickAnswer' &&
    slideType === 'typeAnswer' &&
    quizStatus === QuizStatuses.RESULT_SCREEN &&
    ((getters.isPresentingOrBackstage && !manualRevealCorrectAnswers) ||
      (manualRevealCorrectAnswers && showCorrectOption))
}

const brainstormNextStep = (state) => {
  if (state.slideActive.type !== 'ideas') return null
  const { votingStep } = state.slideActive
  if (!votingStep || votingStep === IdeasTabName.SUBMISSION) return IdeasTabName.VOTING
  if (votingStep === IdeasTabName.VOTING) return IdeasTabName.RESULT
  return null
}

const brainstormPrevStep = (state) => {
  if (state.slideActive.type !== 'ideas') return null
  const prevStepMap = {
    [IdeasTabName.VOTING]: IdeasTabName.SUBMISSION,
    [IdeasTabName.RESULT]: IdeasTabName.VOTING,
  }
  return prevStepMap[state.slideActive.votingStep] || null
}

const brainstormHasNoIdeas = (state) => {
  const options = state.slideActive.SlideOptions
  return !options || options.length === 0
}

export default {
  lastSlide,
  isLastSlide,
  getSlideActive,
  isStartPresentingFromLastSlide,
  isLobbyScreen,
  questionHasImageXLarge,
  isTextColourLight,
  isQuestionImageEnabled,
  isShowFullImageQuestion,
  isQuestionImageAndHasImageXLargeEnabled,
  hasSlideId,
  isDefaultSlide,
  currentSlideType,
  hasSlideTitle,
  slideTitle,
  isQuizSlideType,
  isSlideTitleEmpty,
  isContrastEnough,
  isTypeAnswerButtonVisible,
  brainstormNextStep,
  brainstormPrevStep,
  brainstormHasNoIdeas,
}
